import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Upload } from "lucide-react";

interface IdCardUploadProps {
  contactId: string;
  onUploadComplete: () => void;
}

export const IdCardUpload = ({ contactId, onUploadComplete }: IdCardUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const file = event.target.files?.[0];
      
      if (!file) {
        throw new Error("No file selected");
      }

      if (file.type !== "application/pdf") {
        throw new Error("Only PDF files are allowed");
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("No authenticated session");
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${contactId}/${Math.random()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('id_cards')
        .upload(fileName, file);

      if (uploadError) {
        throw uploadError;
      }

      const { error: dbError } = await supabase
        .from('id_cards')
        .insert({
          user_id: contactId,
          file_path: fileName,
          uploaded_by: session.user.id
        });

      if (dbError) {
        console.error("Database error:", dbError);
        throw new Error("Failed to save ID card record");
      }

      toast({
        title: "Success",
        description: "ID card uploaded successfully"
      });
      
      onUploadComplete();
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error.message
      });
    } finally {
      setUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  return (
    <div className="bg-white border border-gray-200 p-8 rounded-2xl">
      <label htmlFor="id-card-upload" className="cursor-pointer">
        <div className="bg-white border border-[#9b87f5] rounded-xl p-6 flex flex-col items-center gap-2 hover:bg-gray-50 transition-colors">
          <Upload className="h-6 w-6 text-[#9b87f5]" />
          <span className="text-[#9b87f5] font-medium">Upload ID Card</span>
          <input
            id="id-card-upload"
            type="file"
            accept=".pdf"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
          />
        </div>
      </label>
    </div>
  );
};